import api from "./api";

// CR-SEQ-2026-025 — the metadata-driven report engine, templates and
// snapshots. The catalogue is the single source of truth for what can be
// reported; nothing in the client hardcodes fields.

export type CatalogField = {
  name: string;
  label: string;
  dataType:
    | "string"
    | "number"
    | "boolean"
    | "date"
    | "volume"
    | "percent"
    | "notes";
  kind: "dimension" | "measure";
  periodSemantics: "absolute_range" | "accounting_period";
  availableAt: string[];
  operators: FilterOperator[];
};

export type CatalogLevel = {
  name: string;
  label: string;
  grainRank: number;
  grain:
    | "subscriber"
    | "entitlement"
    | "offtake"
    | "allocation"
    | "transaction"
    | "meter_replacement"
    | "water_harvesting_event";
  family:
    | "customer"
    | "allocation"
    | "transaction"
    | "meter_replacement"
    | "water_harvesting_event";
  fields: CatalogField[];
};

export type FilterOperator =
  | "eq"
  | "contains"
  | "starts_with"
  | "gt"
  | "gte"
  | "lt"
  | "lte";

export type ReportFilter = {
  field: string;
  operator: FilterOperator;
  value: string;
};

export type ReportDefinition = {
  level1ResourceId?: string;
  accountingPeriodId?: string;
  dataLevels: string[];
  fields: string[];
  filters: ReportFilter[];
  dateRange: { from: string; to: string };
  announcedAllocations?: "as_at" | "in_range";
};

export type ReportColumn = {
  name: string;
  label: string;
  displayLabel?: string;
  dataType: CatalogField["dataType"];
};

export type ReportScope = {
  id: string;
  name: string;
  accountingPeriods: Array<{
    id: string;
    periodStart: string;
    periodEnd: string;
    isActive: boolean;
  }>;
};

export type ReportRunResult = {
  runId: string;
  resultToken: string;
  reportTemplateId: string | null;
  grain: string;
  /** The range this result was produced for, as the engine resolved it. */
  dateRange: { from: string; to: string };
  /** The scheme the rows were produced under; absent means every scheme. */
  level1ResourceId?: string;
  level1ResourceName?: string;
  accountingPeriod?: {
    id: string;
    periodStart: string;
    periodEnd: string;
  };
  columns: ReportColumn[];
  rows: Record<string, any>[];
  total: number;
  catalogVersion: string;
};

export const getReportCatalog = async (): Promise<CatalogLevel[]> => {
  const { data } = await api.get("/admin/reportCatalog");
  return data.levels;
};

export const getReportScopes = async (): Promise<ReportScope[]> => {
  const { data } = await api.get("/admin/reportScopes");
  return data.schemes;
};

export const runGeneralReport = async (
  definition: ReportDefinition,
): Promise<ReportRunResult> => {
  const { data } = await api.post("/admin/reports/general/run", definition);
  return data;
};

/** Adonis paginator meta, as `getMeta()` returns it. */
export type PaginationMeta = {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
  firstPage: number;
};

// --- Templates (CR §1.3.5) -------------------------------------------------

export type ReportTemplate = {
  id: string;
  name: string;
  createdByUserId: string | null;
  createdBy?: { id: string; name: string } | null;
  lastRunAt: string | null;
  config: {
    catalogVersion: string;
    dataLevels: string[];
    fields: string[];
    filters: ReportFilter[];
  };
  catalogStatus: {
    isRunnable: boolean;
    missing: { dataLevels: string[]; fields: string[]; filterFields: string[] };
    savedCatalogVersion: string;
    currentCatalogVersion: string;
  };
};

export const getReportTemplates = async (
  params?: Record<string, any>,
): Promise<{ reportTemplates: ReportTemplate[]; meta: PaginationMeta }> => {
  const { data } = await api.get("/admin/reportTemplates", { params });
  return data;
};

export const getReportTemplate = async (
  id: string,
): Promise<ReportTemplate> => {
  const { data } = await api.get(`/admin/reportTemplates/${id}`);
  return data.reportTemplate;
};

export const createReportTemplate = async (payload: {
  name: string;
  dataLevels: string[];
  fields: string[];
  filters: ReportFilter[];
}): Promise<ReportTemplate> => {
  const { data } = await api.post("/admin/reportTemplates", payload);
  return data.reportTemplate;
};

export const updateReportTemplate = async (
  id: string,
  payload: {
    name: string;
    dataLevels: string[];
    fields: string[];
    filters: ReportFilter[];
  },
): Promise<ReportTemplate> => {
  const { data } = await api.put(`/admin/reportTemplates/${id}`, payload);
  return data.reportTemplate;
};

export const deleteReportTemplate = async (id: string): Promise<void> => {
  await api.delete(`/admin/reportTemplates/${id}`);
};

export const runReportTemplate = async (
  id: string,
  payload: {
    level1ResourceId?: string;
    accountingPeriodId?: string;
    dateRange: { from: string; to: string };
    announcedAllocations?: "as_at" | "in_range";
  },
): Promise<ReportRunResult & { reportTemplateId: string }> => {
  const { data } = await api.post(`/admin/reportTemplates/${id}/run`, payload);
  return data;
};

// --- Snapshots (CR §1.3.6) -------------------------------------------------

export type ReportSnapshot = {
  id: string;
  name: string;
  generatedByUserId: string;
  generatedBy?: { id: string; name: string };
  fromDate: string;
  toDate: string;
  createdAt: string;
  /** Present on the full snapshot payload; list responses omit large data. */
  catalogVersion?: string;
  reportTemplate: { id: string; name: string; isDeleted: boolean } | null;
  data?: {
    runId: string;
    catalogVersion: string;
    grain: string;
    /** Older frozen snapshots pre-date this explicit business-date field. */
    dateRange?: ReportRunResult["dateRange"];
    level1ResourceId?: string;
    level1ResourceName?: string;
    accountingPeriod?: ReportRunResult["accountingPeriod"];
    columns: ReportColumn[];
    rows: Record<string, any>[];
    total?: number;
  };
};

export const getReportSnapshots = async (
  params?: Record<string, any>,
): Promise<{ reportSnapshots: ReportSnapshot[]; meta: PaginationMeta }> => {
  const { data } = await api.get("/admin/reportSnapshots", { params });
  return data;
};

export const getReportSnapshot = async (
  id: string,
): Promise<ReportSnapshot> => {
  const { data } = await api.get(`/admin/reportSnapshots/${id}`);
  return data.reportSnapshot;
};

export const deleteReportSnapshot = async (id: string): Promise<void> => {
  await api.delete(`/admin/reportSnapshots/${id}`);
};

export const createReportSnapshot = async (payload: {
  name: string;
  resultToken: string;
  rows: Record<string, any>[];
}): Promise<ReportSnapshot> => {
  const { data } = await api.post("/admin/reportSnapshots", payload);
  return data.reportSnapshot;
};

export const auditReportExport = async (payload: {
  format: "csv" | "pdf";
  selectedRowCount: number;
  resultToken?: string;
  reportSnapshotId?: string;
}): Promise<void> => {
  await api.post("/admin/reportExports", payload);
};
